import React, { useState, useContext, useEffect } from 'react';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import environmentConfigs from '../../config/environment-configs';
import { FieldSet } from '../../components/Form/FieldSet/styles';
import { DivProducts } from './styles';
import Button from '../../components/Button';
import { DeleteAccountContext } from '.';
import Checkbox from '../../components/Form/Checkbox';
import Title from '../../components/Title';
import { Actions, Categories, Labels } from '../../types/gtm';
import { useAuth } from '../../hooks/useAuth';

export const Info = styled.h2`
  font-size: 20px;
  line-height: 22px;
  color: #000000;
  margin: 48px 0 24px 0;
`;

const ReasonFormContainer = styled.div`
  width: 626px;
  margin-bottom: 61px;

  @media (max-width: 650px) {
    width: 100%;
  }

  .reasonContainer {
    display: flex;
    align-items: center;

    &:not(:last-child) {
      margin-bottom: 16px;
    }

    > label {
      display: flex;
      color: #333333;
      line-height: 20px;
      margin-bottom: 0;
      @media (max-width: 650px) {
        align-items: center;
      }
    }
  }
  textarea {
    height: 80px;
    padding: 10px;
    margin: 24px 0 0 0;
    border: 1px solid #d5d5d5;
    border-top: 1px solid #888888;
    border-radius: 3px;
    &:focus,
    &:hover {
      outline: none;
      border: 1px solid ${props => props.theme.colors?.primary.main.color};
    }
  }
  form > div:last-child {
    display: flex;
    justify-content: flex-end;
    margin-top: 48px;
    @media (max-width: 650px) {
      button {
        width: 100%;
      }
    }
  }
`;

const DeleteReason = () => {
  const { user } = useAuth();
  const { setUserDeleteReasons, userDeleteReasons, reasons } =
    useContext(DeleteAccountContext);
  const [reasonOpenText, setReasonOpenText] = useState(false);
  const [reasonText, setReasonText] = useState('');

  window.dataLayer = window.dataLayer || [];

  useEffect(() => {
    window.dataLayer.push({
      event: 'customEventUserInteraction',
      event_category: Categories.DeleteAccount,
      event_action: Actions.Pageview,
      event_label: Labels.DeleteAccountPageReasons,
      bd_suser_code: user?.globoId,
    });
  }, [user]);

  const getReasonsAnswer = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const { value } = e.target;
    const index = userDeleteReasons.indexOf(value);
    if (index > -1) {
      setUserDeleteReasons([
        ...userDeleteReasons.slice(0, index),
        ...userDeleteReasons.slice(index + 1),
      ]);
    } else {
      setUserDeleteReasons([...userDeleteReasons, ...[value]]);
    }
  };

  const isSelected = (value: string) => {
    return userDeleteReasons.includes(value);
  };

  const saveReasonsAndConfirmUserAccount = (userReasons: string[]) => {
    userReasons.forEach(reason => {
      window.dataLayer.push({
        event: 'customEventUserInteraction',
        event_category: Categories.DeleteAccount,
        event_action:
          reasonText === reason ? `Motivo - Outro` : `Motivo - ${reason}`,
        event_label:
          reasonText === reason ? `${reason.substring(0, 30)}` : undefined,
        bd_suser_code: user?.globoId,
      });
    });

    const randomReasonsID = `${Math.floor(Math.random() * 999999) + 1}`;

    sessionStorage.setItem(randomReasonsID, userReasons.join('|'));
    localStorage.setItem(
      'redirect',
      `excluir-conta/confirma-senha/${randomReasonsID}/confirm`,
    );

    window.location.href = environmentConfigs.REDIRECT_URL;
  };

  const handleFormSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    toast.dismiss();

    if (userDeleteReasons.length === 0 && reasonText.length === 0) {
      toast.error('Informe uma razão');
      return false;
    }

    let finalReasonsList = [...userDeleteReasons];
    if (reasonText) {
      finalReasonsList = [...finalReasonsList, reasonText];
    }
    saveReasonsAndConfirmUserAccount(finalReasonsList);

    window.dataLayer.push({
      event: 'customEventUserInteraction',
      event_category: Categories.DeleteAccount,
      event_action: Actions.ClickProceed,
      event_label: Labels.DeleteAccountPageReasons,
      bd_suser_code: user?.globoId,
    });

    return false;
  };

  const showButton =
    userDeleteReasons.length > 0 ||
    (reasonOpenText && reasonText.trim().length > 0);

  return (
    <ReasonFormContainer>
      <form onSubmit={handleFormSubmit}>
        <Title>Porque você está excluindo sua Conta Globo?</Title>
        <FieldSet>
          {reasons && (
            <div>
              {reasons.map((value, i) => (
                <div
                  className="reasonContainer"
                  id={`item-${String(i)}`}
                  key={`item-${String(i)}`}
                >
                  <Checkbox
                    name="reason"
                    id={String(i)}
                    value={value}
                    checked={isSelected(value)}
                    onChange={getReasonsAnswer}
                  />
                  <label htmlFor={String(i)}>{value}.</label>
                </div>
              ))}
              <div
                className="reasonContainer"
                id={`item-${String(reasons.length)}`}
              >
                <Checkbox
                  name="reason"
                  value="Outro"
                  id={String(reasons.length)}
                  onChange={e => {
                    if (e.target === e.currentTarget) {
                      setReasonOpenText(!reasonOpenText);
                      if (reasonOpenText) {
                        setReasonText('');
                      }
                    }
                  }}
                />
                <label htmlFor={String(reasons.length)}>Outro.</label>
              </div>
            </div>
          )}

          {reasonOpenText && (
            <textarea
              name="reasonText"
              onChange={e => {
                setReasonText(e.target.value);
              }}
              placeholder="Escreva aqui o motivo de você estar excluindo sua conta."
            />
          )}
        </FieldSet>
        <div>
          {showButton ? (
            <DivProducts>
              <div className="divButton reasonButton">
                <Button type="submit" variant="primary" width="368px">
                  Prosseguir
                </Button>
              </div>
            </DivProducts>
          ) : null}
        </div>
      </form>
    </ReasonFormContainer>
  );
};

export default DeleteReason;
